import { useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/use-toast'
import { useUpdateUser, useUser as useUserData } from '@/hooks/use-api'
import { useUser } from '@/contexts/UserContext'
import type { User, UserType } from '@/types'
import { getUserTypeDisplayName } from '@/lib/userTypeDisplay'

interface EditUserModalProps {
  userId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function EditUserModal({ userId, open, onOpenChange }: EditUserModalProps) {
  const { toast } = useToast()
  const { isSuperAdmin } = useUser()
  const updateMutation = useUpdateUser()
  const { data: userData, isLoading } = useUserData(userId || '')

  // Only Super Admin can assign Super Admin role
  const userTypes: UserType[] = useMemo(() => {
    return isSuperAdmin
      ? ['Super Admin', 'Admin', 'User', 'Marketing', 'BDR', 'Orders', 'Quotes', 'Support', 'RFP']
      : ['Admin', 'User', 'Marketing', 'BDR', 'Orders', 'Quotes', 'Support', 'RFP']
  }, [isSuperAdmin])

  const [formData, setFormData] = useState<Partial<User>>({
    name: '',
    email: '',
    username: '',
    user_type: 'User',
    is_active: true,
    alias: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Load user data when modal opens or userId changes
  useEffect(() => {
    if (userData) {
      setFormData({
        name: userData.name || '',
        email: userData.email || '',
        username: userData.username || '',
        user_type: userData.user_type || 'User',
        is_active: userData.is_active ?? true,
        alias: userData.alias || '',
      })
      setErrors({})
    }
  }, [userData])

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name?.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required'
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Email must be a valid email address'
      }
    }

    if (!formData.username?.trim()) {
      newErrors.username = 'Username is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!userId) {
      toast({
        title: 'Error',
        description: 'No user selected for editing.',
        variant: 'destructive',
      })
      return
    }

    if (!validate()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields correctly.',
        variant: 'destructive',
      })
      return
    }

    try {
      // Remove password field if empty (don't update password if not provided)
      const updateData = { ...formData }
      if (updateData.password === '') {
        delete updateData.password
      }

      await updateMutation.mutateAsync({ id: userId, userData: updateData })

      toast({
        title: 'Success',
        description: 'User updated successfully.',
      })

      onOpenChange(false)
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to update user'
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  const handleChange = (field: keyof typeof formData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent aria-describedby="edit-user-loading">
          <DialogHeader>
            <DialogTitle>Loading User...</DialogTitle>
          </DialogHeader>
          <div id="edit-user-loading" className="text-center py-8 text-muted-foreground">
            Loading user details...
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        aria-describedby="edit-user-description"
      >
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription id="edit-user-description">
            Update the user details below. All fields marked with * are required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold bg-muted px-3 py-1.5 rounded-sm">
              Basic Information
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name" className="required">
                  Full Name *
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  aria-required="true"
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? 'name-error' : undefined}
                />
                {errors.name && (
                  <p id="name-error" className="text-sm text-destructive mt-1" role="alert">
                    {errors.name}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="username" className="required">
                  Username *
                </Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => handleChange('username', e.target.value)}
                  aria-required="true"
                  aria-invalid={!!errors.username}
                  aria-describedby={errors.username ? 'username-error' : undefined}
                />
                {errors.username && (
                  <p id="username-error" className="text-sm text-destructive mt-1" role="alert">
                    {errors.username}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="email" className="required">
                  Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  aria-required="true"
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                />
                {errors.email && (
                  <p id="email-error" className="text-sm text-destructive mt-1" role="alert">
                    {errors.email}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="password">New Password (leave blank to keep current)</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password || ''}
                  onChange={(e) => handleChange('password', e.target.value)}
                  placeholder="Enter new password"
                  aria-describedby="password-help"
                />
                <p id="password-help" className="text-xs text-muted-foreground mt-1">
                  Minimum 8 characters if changing
                </p>
              </div>

              <div>
                <Label htmlFor="alias">Alias</Label>
                <Input
                  id="alias"
                  value={formData.alias || ''}
                  onChange={(e) => handleChange('alias', e.target.value)}
                  maxLength={40}
                />
              </div>
            </div>
          </div>

          {/* Role and Status */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold bg-muted px-3 py-1.5 rounded-sm">Role & Status</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="user_type">User Type</Label>
                <Select
                  value={formData.user_type}
                  onValueChange={(value) => handleChange('user_type', value)}
                >
                  <SelectTrigger id="user_type">
                    <SelectValue placeholder="Select user type" />
                  </SelectTrigger>
                  <SelectContent>
                    {userTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {getUserTypeDisplayName(type)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => handleChange('is_active', checked)}
                />
                <Label htmlFor="is_active">Active User</Label>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Updating...' : 'Update User'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
